import type { PropsWithChildren, ReactNode } from 'react';
import { Children } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Marquee } from '#src/atoms/container/Marquee/index.tsx';
import { Container } from '#src/atoms/layout/Container/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyL, BrandXL } from '#src/atoms/typography/index.tsx';
import type { Pictures } from '#src/components/landing/types.ts';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

const ImageWrapper = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  background-image: var(--background);
  background-size: cover;
  background-position: center bottom;
  border-radius: 20px 20px 0 0;
  min-height: 560px;
  margin-bottom: calc(-1 * var(--quadruple-unit));
  padding-bottom: var(--quadruple-unit);
  &::before {
    position: absolute;
    inset: 0;
    content: '';
    background-image:
      linear-gradient(
        to top,
        var(--c-black),
        var(--c-black) 5%,
        transparent 40%
      ),
      linear-gradient(to right, rgba(var(--c-rgb-black), 0.5), transparent 20%);
  }
`;
const Content = styled(Vertical)`
  position: relative;
  text-align: left;
  max-width: 380px;
  width: 100%;
  margin: auto;
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    position: absolute;
    margin-left: 10%;
    align-items: flex-start;
  }
`;
const LeagueImageWrapper = styled.button`
  border-radius: 20px;
  overflow: hidden;
  width: 300px;
`;

type ScoutAssets = {
  background: Pictures;
  players: Pictures;
};

type Props = {
  assets: ScoutAssets;
  title: ReactNode;
  description: ReactNode;
  ctaLabel: ReactNode;
  players?: ReactNode;
};
export const Scout = ({
  assets,
  children,
  title,
  description,
  ctaLabel,
  players,
}: PropsWithChildren<Props>) => {
  const { signUp } = useConnectionContext();
  return (
    <Vertical
      gap={0}
      style={
        {
          '--background': `image-set(url(${assets.background.avif}) type('image/avif'), url(${assets.background.pictureUrl}) type('image/webp'))`,
        } as CSSProperties
      }
    >
      <Container>
        <ImageWrapper onClick={signUp}>
          <Content gap={5}>
            <Vertical gap={3}>
              <BrandXL>{title}</BrandXL>
              <BodyL>{description}</BodyL>
            </Vertical>
            <Button asSpan color="secondary" size="large">
              {ctaLabel}
            </Button>
          </Content>
          {players}
        </ImageWrapper>
      </Container>
      <Marquee direction="reverse">
        {Children.toArray(children).map((child, index) => (
          <LeagueImageWrapper
            type="button"
            onClick={signUp}
            // oxlint-disable-next-line react/no-array-index-key
            key={index}
          >
            {child}
          </LeagueImageWrapper>
        ))}
      </Marquee>
    </Vertical>
  );
};
